<script module lang="ts">
  export const GraphsMountedContextKey: ContextKey<WritableSignal<Set<symbol>>> =
    Symbol('GraphsMounted');

  export const DebugNowContextKey: ContextKey<() => number | undefined> = Symbol('DebugNow');
</script>

<script lang="ts">
  import { untrack } from 'svelte';
  import { page, navigating } from '$app/state';
  import Breadcrumbs from '#lib/components/page/Breadcrumbs.svelte';
  import Sidebar from '#lib/components/page/Sidebar.svelte';
  import Topbar from '#lib/components/page/Topbar.svelte';
  import { fade } from 'svelte/transition';
  import { mut, type WritableSignal } from '@typek/signalhead';
  import { setContext, type ContextKey } from '#lib/utils/svelte-context.js';

  let isNavigating = $state(false);
  let navDebounce: unknown | undefined;

  function fadeAnimation(navigating: boolean) {
    if (navigating) {
      if (!navDebounce) navDebounce = setTimeout(() => (isNavigating = true), 200);
    } else {
      clearTimeout(navDebounce as number);
      navDebounce = undefined;
      isNavigating = false;
    }
  }

  let { data, children } = $props();

  setContext(DebugNowContextKey, () => (data.debugNow ? +data.debugNow : undefined));
  const mountedGraphs$ = mut(new Set());
  setContext(GraphsMountedContextKey, mountedGraphs$);

  let hideSidebar = $derived(page.url.pathname.split('/').includes('public'));

  $effect(() => {
    const next = Boolean(navigating.to);

    untrack(() => {
      fadeAnimation(next);
    });
  });
</script>

<section class="grid h-screen w-screen overflow-hidden">
  <div
    class="flex place-items-center content-center justify-center overflow-hidden bg-slate-800 px-4 py-2 text-lg font-bold text-white select-none dark:bg-slate-800"
  >
    <img src="/logo_dark.svg" alt="chartium" class="max-h-full max-w-full object-contain" />
  </div>
  <Topbar
    timeRangeSettings={page.data.timeRangeSettings}
    showSyncXButton={$mountedGraphs$.size > 0}
  />
  {#if !hideSidebar}
    <Sidebar feMode={data.feMode} datacenters={data.sidemenu} showHpVmware={data.showHpVmware} />
  {/if}
  <main
    class="bg-base-100 text-base-content overflow-x-hidden px-4 {page.data.mainAsFlex
      ? 'flex flex-col'
      : ''}"
    style={hideSidebar ? 'grid-column-start: 1; grid-column-end: 3;' : 'grid-area:2/2/2/2'}
  >
    <Breadcrumbs breadcrumbs={page.data.breadcrumbs} />
    {@render children?.()}
  </main>
  {#if isNavigating}
    <div
      class="bg-base-100/50 z-50 grid place-items-center backdrop-blur-xs"
      style="grid-area: 2/2/2/2"
      transition:fade|global
    >
      <div class="loading loading-spinner loading-xl"></div>
    </div>
  {/if}
</section>

<style>
  section {
    grid-template: var(--header-height) 1fr / var(--sidebar-width) 1fr;
  }

  main {
    scroll-padding-top: 96px;
  }
</style>
